import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { useVendorDirectoryPermissions } from "../../hooks/useVendorDirectoryPermissions";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import {
  archiveVendorDirectoryVendor,
  getVendorDirectoryVendor,
  listVendorDirectoryAudit,
  publishVendorDirectoryVendor,
  reactivateVendorDirectoryVendor,
  replaceVendorDirectoryHours,
  replaceVendorDirectoryLanguages,
  replaceVendorDirectoryPaymentMethods,
  suspendVendorDirectoryVendor,
  unpublishVendorDirectoryVendor,
  updateVendorDirectoryVendor,
  upsertVendorDirectoryContact,
  upsertVendorDirectoryLocation,
  upsertVendorDirectoryServiceArea,
  addVendorDirectoryNote,
} from "../../api/clientVendorDirectoryApi";
import {
  getVendorVerificationPanel,
  createVerificationCase,
} from "../../api/clientVendorDirectoryVerificationApi";
import { getVendorPrivacyPanel } from "../../api/clientVendorDirectoryPrivacyApi";
import { getVendorRankingExplanation } from "../../api/clientVendorDirectoryRankingApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { formatStatusLabel } from "../../utils/clientPortalFormat";
import { useLocale } from "../../intl";
import { casamapaPublicVendorProfileHref } from "../../constants/casamapaVendorWorkspacePaths";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.vendorWorkspace";
const VENDORS_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.vendors";
const VERIFICATION_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.verification";

const NAV_GROUPS = [
  {
    id: "profile",
    labelKey: "navGroups.profile",
    sections: ["overview", "profile"],
  },
  {
    id: "presence",
    labelKey: "navGroups.presence",
    sections: ["contacts", "locations", "areas", "hours", "languages"],
  },
  {
    id: "operations",
    labelKey: "navGroups.operations",
    sections: ["verification", "ranking", "privacy", "notes", "audit"],
  },
];

const VALID_SECTIONS = new Set(NAV_GROUPS.flatMap((group) => group.sections));

const PAYMENT_CODES = ["cash", "bank_transfer", "credit_card", "nequi", "daviplata", "pse"];

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function translateKey(t, prefix, value) {
  if (!value) return "—";
  const key = `${prefix}.${value}`;
  const translated = t(key);
  return translated !== key ? translated : formatStatusLabel(value);
}

function lifecycleBadgeClass(status) {
  if (status === "active") return "cvd-badge--published";
  if (["suspended", "archived", "inactive"].includes(status)) return "cvd-badge--suspended";
  if (status === "pending_verification") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function publicationBadgeClass(status) {
  if (status === "published") return "cvd-badge--published";
  if (status === "suppressed" || status === "unpublished") return "cvd-badge--suspended";
  if (status === "ready_for_review") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function CompletenessBar({ value }) {
  const pct = Math.min(100, Math.max(0, Math.round(value || 0)));
  return (
    <div className="cvd-vendor-completeness" aria-label={`${pct}%`}>
      <div className="cvd-vendor-completeness__track">
        <div className="cvd-vendor-completeness__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="cvd-vendor-completeness__label">{pct}%</span>
    </div>
  );
}

function SummaryMetric({ label, value }) {
  return (
    <article className="cvd-summary-card">
      <strong>{value ?? 0}</strong>
      <span>{label}</span>
    </article>
  );
}

function VendorInitialsMark({ name }) {
  const initials = (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  return (
    <span className="cvd-vendor-profile__mark" aria-hidden="true">
      {initials || "?"}
    </span>
  );
}

function ManageMenu({ onAction, busy, t }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const runAction = (action) => {
    setOpen(false);
    onAction(action);
  };

  return (
    <div className="cvd-vendor-profile__menu" ref={menuRef}>
      <button
        type="button"
        className="cvd-btn cvd-btn--secondary"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {t(`${COPY_PREFIX}.manageMenu.label`)}
      </button>
      {open ? (
        <div className="cvd-vendor-profile__menu-panel" role="menu">
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VENDORS_PUBLISH}>
            <button type="button" role="menuitem" disabled={busy} onClick={() => runAction("publish")}>
              {t(`${COPY_PREFIX}.actions.publish`)}
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VENDORS_UPDATE}>
            <button type="button" role="menuitem" disabled={busy} onClick={() => runAction("unpublish")}>
              {t(`${COPY_PREFIX}.actions.unpublish`)}
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VENDORS_SUSPEND}>
            <button type="button" role="menuitem" disabled={busy} onClick={() => runAction("suspend")}>
              {t(`${COPY_PREFIX}.actions.suspend`)}
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VENDORS_UPDATE}>
            <button type="button" role="menuitem" disabled={busy} onClick={() => runAction("reactivate")}>
              {t(`${COPY_PREFIX}.actions.reactivate`)}
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VENDORS_UPDATE}>
            <button type="button" role="menuitem" className="is-danger" disabled={busy} onClick={() => runAction("archive")}>
              {t(`${COPY_PREFIX}.actions.archive`)}
            </button>
          </VendorDirectoryPermissionGate>
        </div>
      ) : null}
    </div>
  );
}

function SectionNavDrawer({ open, onClose, activeSection, onSelect, t }) {
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        className="cvd-vendor-profile__drawer-backdrop"
        onClick={onClose}
        aria-label={t(`${COPY_PREFIX}.nav.close`)}
      />
      <aside className="cvd-vendor-profile__drawer" aria-label={t(`${COPY_PREFIX}.tabsLabel`)}>
        <div className="cvd-vendor-profile__drawer-head">
          <h2>{t(`${COPY_PREFIX}.nav.title`)}</h2>
          <button
            type="button"
            className="cvd-vendor-profile__drawer-close"
            onClick={onClose}
            aria-label={t(`${COPY_PREFIX}.nav.close`)}
          >
            ×
          </button>
        </div>
        <nav className="cvd-vendor-profile__drawer-nav">
          {NAV_GROUPS.map((group) => (
            <div key={group.id} className="cvd-vendor-profile__drawer-group">
              <p className="cvd-vendor-profile__drawer-group-label">{t(`${COPY_PREFIX}.${group.labelKey}`)}</p>
              <ul>
                {group.sections.map((sectionId) => (
                  <li key={sectionId}>
                    <button
                      type="button"
                      className={`cvd-vendor-profile__drawer-link${activeSection === sectionId ? " is-active" : ""}`}
                      onClick={() => onSelect(sectionId)}
                    >
                      {t(`${COPY_PREFIX}.tabs.${sectionId}`)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

function VendorProfileHero({ detail, onAction, busy, onOpenNav, t, tv, publicProfileHref }) {
  const vendor = detail.vendor;
  const completeness = Math.round(vendor.profileCompletenessScore || 0);
  const primaryContact =
    (detail.contacts || []).find((contact) => contact.is_primary) || (detail.contacts || [])[0];
  const isPublished = vendor.publicationStatus === "published";

  return (
    <header className="cvd-vendor-profile__hero">
      <div className="cvd-vendor-profile__hero-main">
        <div className="cvd-vendor-profile__identity">
          <VendorInitialsMark name={vendor.displayName} />
          <div className="cvd-vendor-profile__identity-copy">
            <h1 className="cvd-vendor-profile__title">{vendor.displayName}</h1>
            {vendor.shortDescription ? (
              <p className="cvd-vendor-profile__tagline">{vendor.shortDescription}</p>
            ) : null}
            <p className="cvd-vendor-profile__meta">
              {vendor.publicId}
              {vendor.slug ? ` · ${vendor.slug}` : ""}
              {vendor.legalName ? ` · ${vendor.legalName}` : ""}
            </p>
          </div>
        </div>

        <div className="cvd-vendor-profile__badges">
          <span className={`cvd-badge ${lifecycleBadgeClass(vendor.lifecycleStatus)}`}>
            {tv("lifecycle", vendor.lifecycleStatus)}
          </span>
          <span className={`cvd-badge ${publicationBadgeClass(vendor.publicationStatus)}`}>
            {tv("publication", vendor.publicationStatus)}
          </span>
          <span className="cvd-badge cvd-badge--draft">
            {translateKey(t, `${COPY_PREFIX}.verificationSummary`, vendor.verificationSummaryStatus)}
          </span>
        </div>

        {primaryContact ? (
          <p className="cvd-vendor-profile__primary-contact">
            <span>{translateKey(t, `${COPY_PREFIX}.contacts.types`, primaryContact.contact_type)}</span>
            <strong>{primaryContact.value}</strong>
          </p>
        ) : null}
      </div>

      <div className="cvd-vendor-profile__hero-aside">
        <div className="cvd-vendor-profile__completeness">
          <CompletenessBar value={completeness} />
          <span className="cvd-metric-card__hint">{t(`${COPY_PREFIX}.completeness`, { pct: completeness })}</span>
        </div>
        <div className="cvd-vendor-profile__hero-actions">
          {isPublished && publicProfileHref ? (
            <a className="cvd-btn cvd-btn--ghost" href={publicProfileHref} target="_blank" rel="noreferrer">
              {t(`${COPY_PREFIX}.overview.viewPublicProfile`)}
            </a>
          ) : (
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VENDORS_PUBLISH}>
              <button
                type="button"
                className="cvd-btn cvd-btn--primary"
                disabled={busy}
                onClick={() => onAction("publish")}
              >
                {t(`${COPY_PREFIX}.actions.publishToPublicSite`)}
              </button>
            </VendorDirectoryPermissionGate>
          )}
          <button type="button" className="cvd-btn cvd-btn--secondary" onClick={onOpenNav}>
            {t(`${COPY_PREFIX}.nav.open`)}
          </button>
          <ManageMenu onAction={onAction} busy={busy} t={t} />
        </div>
      </div>
    </header>
  );
}

function SectionToolbar({ section, onOpenNav, t }) {
  return (
    <div className="cvd-vendor-profile__section-bar">
      <div className="cvd-vendor-profile__section-bar-copy">
        <span className="cvd-vendor-profile__section-eyebrow">{t(`${COPY_PREFIX}.nav.currentSection`)}</span>
        <h2 className="cvd-vendor-profile__section-title">{t(`${COPY_PREFIX}.tabs.${section}`)}</h2>
      </div>
      <button type="button" className="cvd-btn cvd-btn--ghost" onClick={onOpenNav}>
        {t(`${COPY_PREFIX}.nav.changeSection`)}
      </button>
    </div>
  );
}

function OverviewTab({ detail, t, onNavigate, onPublish, busy, publicProfileHref }) {
  const readiness = detail.readiness || {};
  const vendor = detail.vendor || {};
  const verificationWarnings = [];
  if (vendor.verificationSummaryStatus === "verification_expired") {
    verificationWarnings.push(t(`${COPY_PREFIX}.overview.verificationExpired`));
  }
  if (detail.verificationBadges?.some((badge) => badge.expiresAt && new Date(badge.expiresAt) < new Date())) {
    verificationWarnings.push(t(`${COPY_PREFIX}.overview.badgesExpired`));
  }

  const capabilityItems = [
    vendor.emergencyServiceAvailable ? t(`${COPY_PREFIX}.profile.emergencyService`) : null,
    vendor.remoteServiceAvailable ? t(`${COPY_PREFIX}.profile.remoteService`) : null,
    vendor.acceptsNewCustomers !== false ? t(`${COPY_PREFIX}.profile.acceptsNewCustomers`) : null,
  ].filter(Boolean);

  const quickLinks = [
    { id: "profile", label: t(`${COPY_PREFIX}.tabs.profile`) },
    { id: "contacts", label: t(`${COPY_PREFIX}.tabs.contacts`) },
    { id: "locations", label: t(`${COPY_PREFIX}.tabs.locations`) },
    { id: "hours", label: t(`${COPY_PREFIX}.tabs.hours`) },
    { id: "verification", label: t(`${COPY_PREFIX}.tabs.verification`) },
  ];

  const hasReadinessIssues =
    !readiness.isReady ||
    readiness.blockingIssues?.length ||
    readiness.warnings?.length ||
    verificationWarnings.length;

  const isPublished = vendor.publicationStatus === "published";

  return (
    <div className="cvd-vendor-workspace__panel cvd-vendor-profile__overview">
      <section className="cvd-vendor-workspace__card cvd-public-path">
        <div>
          <h2>{t(`${COPY_PREFIX}.overview.publicSiteTitle`)}</h2>
          <p>
            {isPublished
              ? t(`${COPY_PREFIX}.overview.publishedBody`)
              : t(`${COPY_PREFIX}.overview.unpublishedBody`)}
          </p>
        </div>
        <div className="cvd-vendor-profile__hero-actions">
          {isPublished && publicProfileHref ? (
            <a className="cvd-btn cvd-btn--primary" href={publicProfileHref} target="_blank" rel="noreferrer">
              {t(`${COPY_PREFIX}.overview.viewPublicProfile`)}
            </a>
          ) : (
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VENDORS_PUBLISH}>
              <button type="button" className="cvd-btn cvd-btn--primary" disabled={busy} onClick={onPublish}>
                {t(`${COPY_PREFIX}.actions.publishToPublicSite`)}
              </button>
            </VendorDirectoryPermissionGate>
          )}
        </div>
      </section>
      {vendor.fullDescription || vendor.shortDescription ? (
        <section className="cvd-vendor-workspace__card cvd-vendor-profile__about">
          <h2>{t(`${COPY_PREFIX}.overview.aboutTitle`)}</h2>
          <p>{vendor.fullDescription || vendor.shortDescription}</p>
        </section>
      ) : null}

      {capabilityItems.length ? (
        <section className="cvd-vendor-workspace__card">
          <h2>{t(`${COPY_PREFIX}.overview.capabilitiesTitle`)}</h2>
          <ul className="cvd-vendor-profile__pill-list">
            {capabilityItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {hasReadinessIssues ? (
        <section className="cvd-vendor-workspace__card cvd-vendor-profile__readiness-card">
          <h2>{t(`${COPY_PREFIX}.overview.readinessTitle`)}</h2>
          <p className="cvd-vendor-workspace__readiness">
            {readiness.isReady
              ? t(`${COPY_PREFIX}.overview.ready`)
              : t(`${COPY_PREFIX}.overview.notReady`)}
          </p>
          {readiness.blockingIssues?.length ? (
            <ul className="cvd-vendor-workspace__readiness-list">
              {readiness.blockingIssues.map((issue) => (
                <li key={issue.code}>{issue.message}</li>
              ))}
            </ul>
          ) : null}
          {readiness.warnings?.length ? (
            <>
              <h3>{t(`${COPY_PREFIX}.overview.warningsTitle`)}</h3>
              <ul className="cvd-vendor-workspace__readiness-list">
                {readiness.warnings.map((issue) => (
                  <li key={issue.code}>{issue.message}</li>
                ))}
              </ul>
            </>
          ) : null}
          {verificationWarnings.length ? (
            <>
              <h3>{t(`${COPY_PREFIX}.overview.verificationWarningsTitle`)}</h3>
              <ul className="cvd-vendor-workspace__readiness-list">
                {verificationWarnings.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      ) : null}

      <section className="cvd-vendor-workspace__card">
        <h2>{t(`${COPY_PREFIX}.overview.summaryTitle`)}</h2>
        <div className="cvd-summary-grid cvd-vendor-profile__metrics">
          <SummaryMetric
            label={t(`${COPY_PREFIX}.overview.categories`)}
            value={detail.categories?.filter((category) => category.status === "active").length || 0}
          />
          <SummaryMetric
            label={t(`${COPY_PREFIX}.overview.services`)}
            value={detail.services?.filter((service) => service.status === "active").length || 0}
          />
          <SummaryMetric label={t(`${COPY_PREFIX}.overview.locations`)} value={detail.locations?.length || 0} />
          <SummaryMetric label={t(`${COPY_PREFIX}.overview.serviceAreas`)} value={detail.serviceAreas?.length || 0} />
          <SummaryMetric
            label={t(`${COPY_PREFIX}.overview.publicContacts`)}
            value={detail.contacts?.filter((contact) => contact.is_public).length || 0}
          />
        </div>
      </section>

      <section className="cvd-vendor-workspace__card">
        <h2>{t(`${COPY_PREFIX}.overview.quickEditTitle`)}</h2>
        <div className="cvd-vendor-profile__quick-links">
          {quickLinks.map((link) => (
            <button key={link.id} type="button" className="cvd-vendor-profile__quick-link" onClick={() => onNavigate(link.id)}>
              {link.label}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function ProfileTab({ detail, onSave, saving, t }) {
  const vendor = detail.vendor;
  const [form, setForm] = useState({
    displayName: vendor.displayName || "",
    legalName: vendor.legalName || "",
    slug: vendor.slug || "",
    shortDescription: vendor.shortDescription || "",
    fullDescription: vendor.fullDescription || "",
    emergencyServiceAvailable: vendor.emergencyServiceAvailable || false,
    remoteServiceAvailable: vendor.remoteServiceAvailable || false,
    acceptsNewCustomers: vendor.acceptsNewCustomers !== false,
    expectedUpdatedAt: vendor.updatedAt,
  });

  useEffect(() => {
    setForm({
      displayName: vendor.displayName || "",
      legalName: vendor.legalName || "",
      slug: vendor.slug || "",
      shortDescription: vendor.shortDescription || "",
      fullDescription: vendor.fullDescription || "",
      emergencyServiceAvailable: vendor.emergencyServiceAvailable || false,
      remoteServiceAvailable: vendor.remoteServiceAvailable || false,
      acceptsNewCustomers: vendor.acceptsNewCustomers !== false,
      expectedUpdatedAt: vendor.updatedAt,
    });
  }, [vendor]);

  return (
    <form
      className="cvd-vendor-workspace__card"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      <h2>{t(`${COPY_PREFIX}.profile.title`)}</h2>
      <div className="cvd-field-grid">
        <div className="cvd-field">
          <label htmlFor="displayName">{t(`${COPY_PREFIX}.profile.displayName`)}</label>
          <input
            id="displayName"
            value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
          />
          <span className="cvd-visibility cvd-visibility--public">{t(`${COPY_PREFIX}.public`)}</span>
        </div>
        <div className="cvd-field">
          <label htmlFor="legalName">{t(`${COPY_PREFIX}.profile.legalName`)}</label>
          <input
            id="legalName"
            value={form.legalName}
            onChange={(e) => setForm((f) => ({ ...f, legalName: e.target.value }))}
          />
          <span className="cvd-visibility cvd-visibility--private">{t(`${COPY_PREFIX}.private`)}</span>
        </div>
        <div className="cvd-field">
          <label htmlFor="slug">{t(`${COPY_PREFIX}.profile.slug`)}</label>
          <input id="slug" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} />
        </div>
      </div>
      <div className="cvd-field">
        <label htmlFor="shortDescription">{t(`${COPY_PREFIX}.profile.shortDescription`)}</label>
        <textarea
          id="shortDescription"
          rows={2}
          value={form.shortDescription}
          onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))}
        />
      </div>
      <div className="cvd-field">
        <label htmlFor="fullDescription">{t(`${COPY_PREFIX}.profile.fullDescription`)}</label>
        <textarea
          id="fullDescription"
          rows={5}
          value={form.fullDescription}
          onChange={(e) => setForm((f) => ({ ...f, fullDescription: e.target.value }))}
        />
      </div>
      <label className="cvd-vendor-workspace__checkbox">
        <input
          type="checkbox"
          checked={form.emergencyServiceAvailable}
          onChange={(e) => setForm((f) => ({ ...f, emergencyServiceAvailable: e.target.checked }))}
        />
        {t(`${COPY_PREFIX}.profile.emergencyService`)}
      </label>
      <label className="cvd-vendor-workspace__checkbox">
        <input
          type="checkbox"
          checked={form.remoteServiceAvailable}
          onChange={(e) => setForm((f) => ({ ...f, remoteServiceAvailable: e.target.checked }))}
        />
        {t(`${COPY_PREFIX}.profile.remoteService`)}
      </label>
      <label className="cvd-vendor-workspace__checkbox">
        <input
          type="checkbox"
          checked={form.acceptsNewCustomers}
          onChange={(e) => setForm((f) => ({ ...f, acceptsNewCustomers: e.target.checked }))}
        />
        {t(`${COPY_PREFIX}.profile.acceptsNewCustomers`)}
      </label>
      <div className="cvd-vendor-workspace__form-actions">
        <button type="submit" className="cvd-btn cvd-btn--primary" disabled={saving}>
          {saving ? t(`${COPY_PREFIX}.saving`) : t(`${COPY_PREFIX}.profile.save`)}
        </button>
      </div>
    </form>
  );
}

function ContactsTab({ detail, clientViewId, vendorId, onReload, t }) {
  const [form, setForm] = useState({
    contactType: "whatsapp",
    value: "",
    isPublic: true,
    isPrimary: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await upsertVendorDirectoryContact(clientViewId, vendorId, form);
      setForm({ contactType: "whatsapp", value: "", isPublic: true, isPrimary: false });
      onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cvd-vendor-workspace__panel">
      <section className="cvd-vendor-workspace__card">
        <h2>{t(`${COPY_PREFIX}.contacts.listTitle`)}</h2>
        {(detail.contacts || []).length ? (
          <ul className="cvd-vendor-workspace__list">
            {(detail.contacts || []).map((c) => (
              <li key={c.id} className="cvd-vendor-workspace__list-item">
                <strong>{translateKey(t, `${COPY_PREFIX}.contacts.types`, c.contact_type)}</strong>
                <span>{c.value}</span>
                <div className="cvd-vendor-workspace__list-meta">
                  <span className="cvd-badge cvd-badge--draft">
                    {c.is_public ? t(`${COPY_PREFIX}.public`) : t(`${COPY_PREFIX}.private`)}
                  </span>
                  {c.is_primary ? <span className="cvd-badge cvd-badge--published">{t(`${COPY_PREFIX}.primary`)}</span> : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="cvd-run-empty">{t(`${COPY_PREFIX}.contacts.empty`)}</p>
        )}
      </section>

      <form className="cvd-vendor-workspace__card" onSubmit={submit}>
        <h2>{t(`${COPY_PREFIX}.contacts.addTitle`)}</h2>
        <div className="cvd-field-grid">
          <div className="cvd-field">
            <label htmlFor="contactType">{t(`${COPY_PREFIX}.contacts.type`)}</label>
            <select
              id="contactType"
              value={form.contactType}
              onChange={(e) => setForm((f) => ({ ...f, contactType: e.target.value }))}
            >
              {["phone", "whatsapp", "email", "website"].map((type) => (
                <option key={type} value={type}>
                  {t(`${COPY_PREFIX}.contacts.types.${type}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="cvd-field">
            <label htmlFor="contactValue">{t(`${COPY_PREFIX}.contacts.value`)}</label>
            <input
              id="contactValue"
              required
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
            />
          </div>
        </div>
        <label className="cvd-vendor-workspace__checkbox">
          <input
            type="checkbox"
            checked={form.isPublic}
            onChange={(e) => setForm((f) => ({ ...f, isPublic: e.target.checked }))}
          />
          {t(`${COPY_PREFIX}.contacts.isPublic`)}
        </label>
        <div className="cvd-vendor-workspace__form-actions">
          <button type="submit" className="cvd-btn cvd-btn--primary" disabled={saving}>
            {t(`${COPY_PREFIX}.contacts.save`)}
          </button>
        </div>
      </form>
    </div>
  );
}

function LocationsTab({ detail, clientViewId, vendorId, onReload, t }) {
  const [form, setForm] = useState({
    locationType: "office",
    addressLine1: "",
    latitude: "",
    longitude: "",
    isPublic: false,
    isPrimary: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await upsertVendorDirectoryLocation(clientViewId, vendorId, {
        ...form,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
      });
      onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cvd-vendor-workspace__panel">
      <section className="cvd-vendor-workspace__card">
        <h2>{t(`${COPY_PREFIX}.locations.listTitle`)}</h2>
        {(detail.locations || []).length ? (
          <ul className="cvd-vendor-workspace__list">
            {(detail.locations || []).map((loc) => (
              <li key={loc.id} className="cvd-vendor-workspace__list-item">
                <strong>{translateKey(t, `${COPY_PREFIX}.locations.types`, loc.location_type)}</strong>
                <span>{loc.address_line_1 || t(`${COPY_PREFIX}.noAddress`)}</span>
                <span className="cvd-badge cvd-badge--draft">
                  {loc.is_public ? t(`${COPY_PREFIX}.public`) : t(`${COPY_PREFIX}.private`)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="cvd-run-empty">{t(`${COPY_PREFIX}.locations.empty`)}</p>
        )}
      </section>

      <form className="cvd-vendor-workspace__card" onSubmit={submit}>
        <h2>{t(`${COPY_PREFIX}.locations.addTitle`)}</h2>
        <div className="cvd-field-grid">
          <div className="cvd-field">
            <label htmlFor="locationType">{t(`${COPY_PREFIX}.locations.type`)}</label>
            <select
              id="locationType"
              value={form.locationType}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  locationType: e.target.value,
                  isPublic: e.target.value === "home" ? false : f.isPublic,
                }))
              }
            >
              {["office", "home", "warehouse"].map((type) => (
                <option key={type} value={type}>
                  {t(`${COPY_PREFIX}.locations.types.${type}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="cvd-field">
            <label htmlFor="addressLine1">{t(`${COPY_PREFIX}.locations.address`)}</label>
            <input
              id="addressLine1"
              value={form.addressLine1}
              onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))}
            />
          </div>
          <div className="cvd-field">
            <label htmlFor="latitude">{t(`${COPY_PREFIX}.locations.latitude`)}</label>
            <input
              id="latitude"
              value={form.latitude}
              onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
            />
          </div>
          <div className="cvd-field">
            <label htmlFor="longitude">{t(`${COPY_PREFIX}.locations.longitude`)}</label>
            <input
              id="longitude"
              value={form.longitude}
              onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
            />
          </div>
        </div>
        {form.locationType === "home" ? (
          <p className="cvd-badge cvd-badge--warning" role="status">
            {t(`${COPY_PREFIX}.locations.homePrivacyNote`)}
          </p>
        ) : null}
        <label className="cvd-vendor-workspace__checkbox">
          <input
            type="checkbox"
            checked={form.isPublic}
            onChange={(e) => setForm((f) => ({ ...f, isPublic: e.target.checked }))}
          />
          {t(`${COPY_PREFIX}.locations.isPublic`)}
        </label>
        <div className="cvd-vendor-workspace__form-actions">
          <button type="submit" className="cvd-btn cvd-btn--primary" disabled={saving}>
            {t(`${COPY_PREFIX}.locations.save`)}
          </button>
        </div>
      </form>
    </div>
  );
}

function ServiceAreasTab({ detail, clientViewId, vendorId, onReload, t }) {
  const [form, setForm] = useState({
    serviceAreaType: "municipality",
    municipalityId: "",
    radiusKm: "",
    centerLatitude: "",
    centerLongitude: "",
    isPrimary: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await upsertVendorDirectoryServiceArea(clientViewId, vendorId, {
        ...form,
        municipalityId: form.municipalityId || null,
        radiusKm: form.radiusKm ? Number(form.radiusKm) : null,
        centerLatitude: form.centerLatitude ? Number(form.centerLatitude) : null,
        centerLongitude: form.centerLongitude ? Number(form.centerLongitude) : null,
      });
      onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cvd-vendor-workspace__panel">
      <section className="cvd-vendor-workspace__card">
        <h2>{t(`${COPY_PREFIX}.areas.listTitle`)}</h2>
        {(detail.serviceAreas || []).length ? (
          <ul className="cvd-vendor-workspace__list">
            {(detail.serviceAreas || []).map((area) => (
              <li key={area.id} className="cvd-vendor-workspace__list-item">
                <strong>{translateKey(t, `${COPY_PREFIX}.areas.types`, area.service_area_type)}</strong>
                <span>
                  {area.municipality_name ? area.municipality_name : ""}
                  {area.radius_km ? ` · ${area.radius_km} km` : ""}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="cvd-run-empty">{t(`${COPY_PREFIX}.areas.empty`)}</p>
        )}
      </section>

      <form className="cvd-vendor-workspace__card" onSubmit={submit}>
        <h2>{t(`${COPY_PREFIX}.areas.addTitle`)}</h2>
        <div className="cvd-field-grid">
          <div className="cvd-field">
            <label htmlFor="serviceAreaType">{t(`${COPY_PREFIX}.areas.type`)}</label>
            <select
              id="serviceAreaType"
              value={form.serviceAreaType}
              onChange={(e) => setForm((f) => ({ ...f, serviceAreaType: e.target.value }))}
            >
              {["municipality", "radius", "remote", "onsite_at_vendor_only"].map((type) => (
                <option key={type} value={type}>
                  {t(`${COPY_PREFIX}.areas.types.${type}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="cvd-field">
            <label htmlFor="radiusKm">{t(`${COPY_PREFIX}.areas.radiusKm`)}</label>
            <input
              id="radiusKm"
              value={form.radiusKm}
              onChange={(e) => setForm((f) => ({ ...f, radiusKm: e.target.value }))}
            />
          </div>
        </div>
        <div className="cvd-vendor-workspace__form-actions">
          <button type="submit" className="cvd-btn cvd-btn--primary" disabled={saving}>
            {t(`${COPY_PREFIX}.areas.save`)}
          </button>
        </div>
      </form>
    </div>
  );
}

function HoursTab({ detail, clientViewId, vendorId, onReload, t }) {
  const [periods, setPeriods] = useState(
    (detail.hours || []).map((h) => ({
      dayOfWeek: h.day_of_week,
      opensAt: h.opens_at,
      closesAt: h.closes_at,
      isClosed: h.is_closed,
      is24Hours: h.is_24_hours,
    }))
  );
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await replaceVendorDirectoryHours(clientViewId, vendorId, periods);
      onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="cvd-vendor-workspace__card" onSubmit={submit}>
      <h2>{t(`${COPY_PREFIX}.hours.title`)}</h2>
      {[0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
        const period = periods.find((p) => p.dayOfWeek === dayOfWeek) || {
          dayOfWeek,
          opensAt: "08:00",
          closesAt: "17:00",
          isClosed: dayOfWeek === 0,
          is24Hours: false,
        };
        return (
          <div key={dayOfWeek} className="cvd-vendor-workspace__hours-row">
            <span className="cvd-vendor-workspace__hours-day">{t(`${COPY_PREFIX}.hours.days.${dayOfWeek}`)}</span>
            <label className="cvd-vendor-workspace__checkbox">
              <input
                type="checkbox"
                checked={period.isClosed}
                onChange={(e) => {
                  const next = periods.filter((p) => p.dayOfWeek !== dayOfWeek);
                  next.push({ ...period, isClosed: e.target.checked });
                  setPeriods(next.sort((a, b) => a.dayOfWeek - b.dayOfWeek));
                }}
              />
              {t(`${COPY_PREFIX}.closed`)}
            </label>
            <input
              type="time"
              value={period.opensAt || ""}
              disabled={period.isClosed}
              onChange={(e) => {
                const next = periods.filter((p) => p.dayOfWeek !== dayOfWeek);
                next.push({ ...period, opensAt: e.target.value });
                setPeriods(next.sort((a, b) => a.dayOfWeek - b.dayOfWeek));
              }}
            />
            <input
              type="time"
              value={period.closesAt || ""}
              disabled={period.isClosed}
              onChange={(e) => {
                const next = periods.filter((p) => p.dayOfWeek !== dayOfWeek);
                next.push({ ...period, closesAt: e.target.value });
                setPeriods(next.sort((a, b) => a.dayOfWeek - b.dayOfWeek));
              }}
            />
          </div>
        );
      })}
      <div className="cvd-vendor-workspace__form-actions">
        <button type="submit" className="cvd-btn cvd-btn--primary" disabled={saving}>
          {t(`${COPY_PREFIX}.hours.save`)}
        </button>
      </div>
    </form>
  );
}

function LanguagesPaymentsTab({ detail, clientViewId, vendorId, onReload, t }) {
  const [languages, setLanguages] = useState([{ languageCode: "es", isPrimary: true }]);
  const [payments, setPayments] = useState([{ paymentMethodCode: "cash" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (detail.languages?.length) {
      setLanguages(detail.languages.map((l) => ({ languageCode: l.language_code, isPrimary: l.is_primary })));
    }
    if (detail.paymentMethods?.length) {
      setPayments(detail.paymentMethods.map((p) => ({ paymentMethodCode: p.payment_method_code })));
    }
  }, [detail]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await replaceVendorDirectoryLanguages(clientViewId, vendorId, languages);
      await replaceVendorDirectoryPaymentMethods(clientViewId, vendorId, payments);
      onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="cvd-vendor-workspace__card" onSubmit={submit}>
      <h2>{t(`${COPY_PREFIX}.languages.title`)}</h2>
      <div className="cvd-field">
        <label htmlFor="languageCode">{t(`${COPY_PREFIX}.languages.primaryLanguage`)}</label>
        <select
          id="languageCode"
          value={languages[0]?.languageCode || "es"}
          onChange={(e) => setLanguages([{ languageCode: e.target.value, isPrimary: true }])}
        >
          {["es", "en"].map((code) => (
            <option key={code} value={code}>
              {t(`${COPY_PREFIX}.languages.languageOptions.${code}`)}
            </option>
          ))}
        </select>
      </div>
      <div className="cvd-field">
        <label htmlFor="paymentMethod">{t(`${COPY_PREFIX}.languages.paymentMethods`)}</label>
        <select
          id="paymentMethod"
          multiple
          value={payments.map((p) => p.paymentMethodCode)}
          onChange={(e) =>
            setPayments(Array.from(e.target.selectedOptions).map((opt) => ({ paymentMethodCode: opt.value })))
          }
        >
          {PAYMENT_CODES.map((code) => (
            <option key={code} value={code}>
              {t(`${COPY_PREFIX}.languages.paymentOptions.${code}`)}
            </option>
          ))}
        </select>
        <p className="cvd-metric-card__hint">{t(`${COPY_PREFIX}.languages.paymentDisclaimer`)}</p>
      </div>
      <div className="cvd-vendor-workspace__form-actions">
        <button type="submit" className="cvd-btn cvd-btn--primary" disabled={saving}>
          {t(`${COPY_PREFIX}.languages.save`)}
        </button>
      </div>
    </form>
  );
}

function NotesTab({ clientViewId, vendorId, t }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await addVendorDirectoryNote(clientViewId, vendorId, { body: note, visibilityClass: "admin_only" });
      setNote("");
      setMessage(t(`${COPY_PREFIX}.notes.saved`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="cvd-vendor-workspace__card" onSubmit={submit}>
      <h2>{t(`${COPY_PREFIX}.notes.title`)}</h2>
      <textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} required />
      {message ? (
        <p className="cvd-metric-card__hint" role="status">
          {message}
        </p>
      ) : null}
      <div className="cvd-vendor-workspace__form-actions">
        <button type="submit" className="cvd-btn cvd-btn--primary" disabled={saving}>
          {t(`${COPY_PREFIX}.notes.save`)}
        </button>
      </div>
    </form>
  );
}

function AuditTab({ clientViewId, vendorId, t, locale }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listVendorDirectoryAudit(clientViewId, vendorId)
      .then((body) => setEvents(body.events || []))
      .finally(() => setLoading(false));
  }, [clientViewId, vendorId]);

  if (loading) return <ClientPortalLoading message={t(`${COPY_PREFIX}.audit.loading`)} />;

  return (
    <section className="cvd-vendor-workspace__card">
      <h2>{t(`${COPY_PREFIX}.audit.title`)}</h2>
      {events.length ? (
        <ul className="cvd-vendor-workspace__list">
          {events.map((event) => (
            <li key={event.audit_id} className="cvd-vendor-workspace__list-item">
              <strong>{event.action}</strong>
              <span>{formatDateTime(event.created_at, locale)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="cvd-run-empty">{t(`${COPY_PREFIX}.audit.empty`)}</p>
      )}
    </section>
  );
}

function RankingTab({ clientViewId, vendorId, t }) {
  const { permissions } = useVendorDirectoryPermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const canView = permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.RANKING_VIEW);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getVendorRankingExplanation(clientViewId, vendorId, { categoryKey: "plumbing" });
      setData(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.ranking.loadError`));
    } finally {
      setLoading(false);
    }
  }, [canView, clientViewId, vendorId, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canView) {
    return <p className="cvd-run-empty">{t(`${COPY_PREFIX}.ranking.noPermission`)}</p>;
  }
  if (loading) return <ClientPortalLoading message={t(`${COPY_PREFIX}.ranking.loading`)} />;
  if (errorMessage) return <ClientPortalError message={errorMessage} onRetry={load} />;

  const snap = data?.snapshot;
  return (
    <section className="cvd-vendor-workspace__card">
      <h2>{t(`${COPY_PREFIX}.ranking.title`)}</h2>
      {snap ? (
        <>
          <p className="cvd-vendor-workspace__readiness">
            {t(`${COPY_PREFIX}.ranking.score`)}: {snap.overallScore ?? "—"} · {t(`${COPY_PREFIX}.ranking.confidence`)}:{" "}
            {snap.confidenceScore ?? "—"} · {t(`${COPY_PREFIX}.ranking.status`)}:{" "}
            {snap.qualificationStatus || snap.eligibilityStatus}
          </p>
          <ul className="cvd-vendor-workspace__readiness-list">
            {(data.explanation?.statements || []).map((item, index) => (
              <li key={index}>{item.text}</li>
            ))}
          </ul>
        </>
      ) : (
        <p className="cvd-run-empty">{t(`${COPY_PREFIX}.ranking.empty`)}</p>
      )}
    </section>
  );
}

function VerificationTab({ clientViewId, vendorId, t, locale }) {
  const { serviciosBase } = useVendorDirectoryPaths();
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getVendorVerificationPanel(clientViewId, vendorId);
      setPanel(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.verification.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, vendorId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const createCase = async () => {
    setBusy(true);
    try {
      await createVerificationCase(clientViewId, { vendorId, caseType: "initial_verification" });
      await load();
    } catch (error) {
      window.alert(error.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <ClientPortalLoading message={t(`${COPY_PREFIX}.verification.loading`)} />;
  if (errorMessage) return <ClientPortalError message={errorMessage} onRetry={load} />;

  const queuePath = `${serviciosBase}/verificacion`;

  return (
    <div className="cvd-vendor-workspace__panel">
      <section className="cvd-vendor-workspace__card">
        <h2>{t(`${COPY_PREFIX}.verification.title`)}</h2>
        <p className="cvd-vendor-workspace__readiness">
          {t(`${COPY_PREFIX}.verification.summary`)}:{" "}
          {translateKey(t, `${COPY_PREFIX}.verificationSummary`, panel?.verificationSummaryStatus || "unverified")}
        </p>
        <div className="cvd-vendor-workspace__actions">
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VERIFICATION_PERFORM}>
            <button type="button" className="cvd-btn cvd-btn--primary" disabled={busy} onClick={createCase}>
              {t(`${COPY_PREFIX}.verification.createCase`)}
            </button>
          </VendorDirectoryPermissionGate>
          <Link className="cvd-btn cvd-btn--secondary" to={queuePath}>
            {t(`${COPY_PREFIX}.verification.openQueue`)}
          </Link>
        </div>
      </section>

      {panel?.activeBadges?.length ? (
        <section className="cvd-vendor-workspace__card">
          <h2>{t(`${COPY_PREFIX}.verification.badgesTitle`)}</h2>
          <ul className="cvd-vendor-workspace__list">
            {panel.activeBadges.map((badge) => (
              <li key={badge.id} className="cvd-vendor-workspace__list-item">
                <strong>{badge.publicLabelEs || badge.badgeKey}</strong>
                {badge.expiresAt ? (
                  <span>{t(`${COPY_PREFIX}.verification.expires`, { date: formatDateTime(badge.expiresAt, locale) })}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <section className="cvd-vendor-workspace__card">
          <p className="cvd-run-empty">{t(`${COPY_PREFIX}.verification.badgesEmpty`)}</p>
        </section>
      )}

      {panel?.openCases?.length ? (
        <section className="cvd-vendor-workspace__card">
          <h2>{t(`${COPY_PREFIX}.verification.openCasesTitle`)}</h2>
          <ul className="cvd-vendor-workspace__list">
            {panel.openCases.map((item) => (
              <li key={item.id} className="cvd-vendor-workspace__list-item">
                <Link to={`${queuePath}/${item.id}`}>
                  {t(`${COPY_PREFIX}.verification.caseLink`, {
                    id: item.id,
                    status: translateKey(t, `${VERIFICATION_PREFIX}.status`, item.status),
                  })}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function PrivacyTab({ clientViewId, vendorId, t }) {
  const { serviciosBase } = useVendorDirectoryPaths();
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getVendorPrivacyPanel(clientViewId, vendorId);
      setPanel(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.privacy.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, vendorId, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <ClientPortalLoading message={t(`${COPY_PREFIX}.privacy.loading`)} />;
  if (errorMessage) return <ClientPortalError message={errorMessage} onRetry={load} />;

  const queuePath = `${serviciosBase}/privacidad`;

  return (
    <div className="cvd-vendor-workspace__panel">
      {panel?.profileSuppression ? (
        <section className="cvd-vendor-workspace__card">
          <h2>{t(`${COPY_PREFIX}.privacy.suppressedTitle`)}</h2>
          <p className="cvd-vendor-workspace__readiness">{t(`${COPY_PREFIX}.privacy.suppressedBody`)}</p>
        </section>
      ) : null}
      {panel?.homeBasedWarnings?.length ? (
        <section className="cvd-vendor-workspace__card">
          <h2>{t(`${COPY_PREFIX}.privacy.locationWarningsTitle`)}</h2>
          <ul className="cvd-vendor-workspace__readiness-list">
            {panel.homeBasedWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </section>
      ) : null}
      <section className="cvd-vendor-workspace__card">
        <h2>{t(`${COPY_PREFIX}.privacy.openRequestsTitle`)}</h2>
        {panel?.openPrivacyRequests?.length ? (
          <ul className="cvd-vendor-workspace__list">
            {panel.openPrivacyRequests.map((r) => (
              <li key={r.id} className="cvd-vendor-workspace__list-item">
                <Link to={`${queuePath}/${r.id}`}>
                  {r.trackingReference || r.id} — {formatStatusLabel(r.requestType)}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="cvd-run-empty">{t(`${COPY_PREFIX}.privacy.openRequestsEmpty`)}</p>
        )}
      </section>
      <section className="cvd-vendor-workspace__card">
        <h2>{t(`${COPY_PREFIX}.privacy.retentionTitle`)}</h2>
        <div className="cvd-summary-grid">
          <SummaryMetric label={t(`${COPY_PREFIX}.privacy.preferences`)} value={panel?.communicationPreferences?.length || 0} />
          <SummaryMetric label={t(`${COPY_PREFIX}.privacy.consentRecords`)} value={panel?.consentHistory?.length || 0} />
          <SummaryMetric label={t(`${COPY_PREFIX}.privacy.activeHolds`)} value={panel?.holds?.length || 0} />
        </div>
      </section>
    </div>
  );
}

export default function ClientVendorDirectoryWorkspacePage() {
  const { clientViewId, vendorId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [navOpen, setNavOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  const sectionParam = searchParams.get("section");
  const activeSection = VALID_SECTIONS.has(sectionParam) ? sectionParam : "overview";

  const setActiveSection = useCallback(
    (sectionId) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (sectionId === "overview") {
            next.delete("section");
          } else {
            next.set("section", sectionId);
          }
          return next;
        },
        { replace: true }
      );
      setNavOpen(false);
    },
    [setSearchParams]
  );

  const tv = useCallback(
    (group, status) => translateKey(t, `${VENDORS_PREFIX}.${group}`, status),
    [t]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getVendorDirectoryVendor(clientViewId, vendorId);
      setDetail(body.vendor);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, vendorId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleHeaderAction = async (action) => {
    setBusy(true);
    setErrorMessage("");
    try {
      if (action === "publish") await publishVendorDirectoryVendor(clientViewId, vendorId);
      if (action === "unpublish") await unpublishVendorDirectoryVendor(clientViewId, vendorId);
      if (action === "suspend") {
        const reason = window.prompt(t(`${COPY_PREFIX}.confirm.suspendPrompt`));
        if (!reason) return;
        await suspendVendorDirectoryVendor(clientViewId, vendorId, { reason });
      }
      if (action === "reactivate") await reactivateVendorDirectoryVendor(clientViewId, vendorId);
      if (action === "archive") {
        if (!window.confirm(t(`${COPY_PREFIX}.confirm.archive`))) return;
        await archiveVendorDirectoryVendor(clientViewId, vendorId, { reason: "admin_action" });
      }
      await load();
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.actionError`));
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async (form) => {
    setProfileSaving(true);
    try {
      const body = await updateVendorDirectoryVendor(clientViewId, vendorId, form);
      setDetail(body.vendor);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.saveProfileError`));
    } finally {
      setProfileSaving(false);
    }
  };

  const basePath = `${serviciosBase}/proveedores`;

  if (loading) return <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} />;
  if (errorMessage && !detail) return <ClientPortalError message={errorMessage} onRetry={load} />;

  return (
    <div className="page-content cvd-admin cvd-vendor-workspace cvd-vendor-profile">
      <Link className="cvd-btn cvd-btn--ghost cvd-vendor-workspace__back" to={basePath}>
        ← {t(`${COPY_PREFIX}.backToList`)}
      </Link>

      {detail ? (
        <VendorProfileHero
          detail={detail}
          onAction={handleHeaderAction}
          busy={busy}
          onOpenNav={() => setNavOpen(true)}
          t={t}
          tv={tv}
          publicProfileHref={
            detail.vendor?.slug
              ? casamapaPublicVendorProfileHref(detail.vendor.slug, locale)
              : null
          }
        />
      ) : null}

      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      <SectionNavDrawer
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activeSection={activeSection}
        onSelect={setActiveSection}
        t={t}
      />

      {detail && activeSection !== "overview" ? (
        <SectionToolbar section={activeSection} onOpenNav={() => setNavOpen(true)} t={t} />
      ) : null}

      {detail ? (
        <div className="cvd-vendor-profile__content">
          {activeSection === "overview" ? (
            <OverviewTab
              detail={detail}
              t={t}
              onNavigate={setActiveSection}
              onPublish={() => handleHeaderAction("publish")}
              busy={busy}
              publicProfileHref={
                detail.vendor?.slug
                  ? casamapaPublicVendorProfileHref(detail.vendor.slug, locale)
                  : null
              }
            />
          ) : null}
          {activeSection === "profile" ? <ProfileTab detail={detail} onSave={saveProfile} saving={profileSaving} t={t} /> : null}
          {activeSection === "contacts" ? (
            <ContactsTab detail={detail} clientViewId={clientViewId} vendorId={vendorId} onReload={load} t={t} />
          ) : null}
          {activeSection === "locations" ? (
            <LocationsTab detail={detail} clientViewId={clientViewId} vendorId={vendorId} onReload={load} t={t} />
          ) : null}
          {activeSection === "areas" ? (
            <ServiceAreasTab detail={detail} clientViewId={clientViewId} vendorId={vendorId} onReload={load} t={t} />
          ) : null}
          {activeSection === "hours" ? (
            <HoursTab detail={detail} clientViewId={clientViewId} vendorId={vendorId} onReload={load} t={t} />
          ) : null}
          {activeSection === "languages" ? (
            <LanguagesPaymentsTab detail={detail} clientViewId={clientViewId} vendorId={vendorId} onReload={load} t={t} />
          ) : null}
          {activeSection === "verification" ? (
            <VerificationTab clientViewId={clientViewId} vendorId={vendorId} t={t} locale={locale} />
          ) : null}
          {activeSection === "ranking" ? <RankingTab clientViewId={clientViewId} vendorId={vendorId} t={t} /> : null}
          {activeSection === "privacy" ? <PrivacyTab clientViewId={clientViewId} vendorId={vendorId} t={t} /> : null}
          {activeSection === "notes" ? <NotesTab clientViewId={clientViewId} vendorId={vendorId} t={t} /> : null}
          {activeSection === "audit" ? <AuditTab clientViewId={clientViewId} vendorId={vendorId} t={t} locale={locale} /> : null}
        </div>
      ) : null}
    </div>
  );
}
